import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Package, ShoppingCart, Heart, Coins, Star, Plus, TrendingUp, MessageCircle, Loader2, BarChart3, CreditCard, QrCode, FileText, Bitcoin, Percent, ArrowUpRight, ArrowDownRight, Pencil, Trash2 } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import api from '../lib/api';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'listings';
  const [tab, setTab] = useState(initialTab);
  const [listings, setListings] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [escrowPurchases, setEscrowPurchases] = useState([]);
  const [escrowSales, setEscrowSales] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  // Review state
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewedTxs, setReviewedTxs] = useState(new Set());

  // Delete listing state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteListing = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/products/${deleteTarget._id}`);
      setListings(prev => prev.filter(p => p._id !== deleteTarget._id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao remover anúncio');
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [listRes, purchRes, saleRes, escrowPurchRes, escrowSaleRes] = await Promise.all([
          api.get('/products/my-listings'),
          api.get('/transactions/my-purchases'),
          api.get('/transactions/my-sales'),
          api.get('/escrow/my-purchases').catch(() => ({ data: { transactions: [] } })),
          api.get('/escrow/my-sales').catch(() => ({ data: { transactions: [] } })),
        ]);
        setListings(listRes.data.products);
        setPurchases(purchRes.data.transactions);
        setSales(saleRes.data.transactions);
        setEscrowPurchases(escrowPurchRes.data.transactions);
        setEscrowSales(escrowSaleRes.data.transactions);

        // Load favorites
        if (user?.favorites?.length > 0) {
          const favProds = [];
          for (const fid of user.favorites.slice(0, 20)) {
            try {
              const r = await api.get(`/products/${fid}`);
              favProds.push(r.data.product);
            } catch {}
          }
          setFavorites(favProds);
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [user]);

  const stats = [
    { label: 'Anúncios Ativos', value: listings.filter(l => l.status === 'active').length, icon: Package, color: 'text-blue-600 bg-blue-50' },
    { label: 'Vendas', value: sales.length, icon: TrendingUp, color: 'text-green-600 bg-green-50' },
    { label: 'Compras', value: purchases.length, icon: ShoppingCart, color: 'text-purple-600 bg-purple-50' },
    { label: 'Saldo', value: `R$ ${(user?.credits || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, icon: Coins, color: 'text-amber-600 bg-amber-50' },
  ];

  const statusMap = { pending: 'Pendente', shipped: 'Enviado', delivered: 'Entregue', completed: 'Concluído', cancelled: 'Cancelado' };
  const statusColor = { pending: 'bg-amber-50 text-amber-700', shipped: 'bg-blue-50 text-blue-700', delivered: 'bg-green-50 text-green-700', completed: 'bg-emerald-50 text-emerald-700', cancelled: 'bg-red-50 text-red-700' };

  const escrowStatusMap = {
    awaiting_payment: 'Aguardando Pagamento', paid: 'Pago (Em Custódia)', shipped: 'Enviado',
    delivered: 'Entregue', completed: 'Concluído', disputed: 'Em Disputa',
    refunded: 'Reembolsado', cancelled: 'Cancelado', expired: 'Expirado'
  };
  const escrowStatusColor = {
    awaiting_payment: 'bg-amber-50 text-amber-700', paid: 'bg-blue-50 text-blue-700',
    shipped: 'bg-indigo-50 text-indigo-700', delivered: 'bg-green-50 text-green-700',
    completed: 'bg-emerald-50 text-emerald-700', disputed: 'bg-red-50 text-red-700',
    refunded: 'bg-orange-50 text-orange-700', cancelled: 'bg-zinc-100 text-zinc-600',
    expired: 'bg-zinc-100 text-zinc-500'
  };

  const handleEscrowAction = async (txId, action, reason = '') => {
    try {
      const { data } = await api.put(`/escrow/transaction/${txId}/action`, { action, reason });
      alert(data.message);
      // Reload escrow data
      const [ep, es] = await Promise.all([
        api.get('/escrow/my-purchases').catch(() => ({ data: { transactions: [] } })),
        api.get('/escrow/my-sales').catch(() => ({ data: { transactions: [] } })),
      ]);
      setEscrowPurchases(ep.data.transactions);
      setEscrowSales(es.data.transactions);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const handleDispute = (txId) => {
    const reason = prompt('Descreva o motivo da disputa:');
    if (reason) handleEscrowAction(txId, 'dispute', reason);
  };

  const openReview = (tx, reviewedId, reviewedName) => {
    setReviewTarget({ tx_id: tx._id, reviewed_id: reviewedId, reviewed_name: reviewedName });
    setReviewRating(0);
    setReviewHover(0);
    setReviewComment('');
    setReviewDialog(true);
  };

  const submitReview = async () => {
    if (reviewRating < 1 || !reviewTarget) return;
    setReviewSubmitting(true);
    try {
      await api.post('/reviews', {
        transaction_id: reviewTarget.tx_id,
        reviewed_id: reviewTarget.reviewed_id,
        rating: reviewRating,
        comment: reviewComment.trim(),
      });
      setReviewedTxs(prev => new Set([...prev, reviewTarget.tx_id]));
      setReviewDialog(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao enviar avaliação');
    } finally {
      setReviewSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Olá, {user?.name}!</p>
        </div>
        <Link to="/create-listing">
          <Button className="gap-2 rounded-full" data-testid="dash-create-listing-btn"><Plus className="h-4 w-4" /> Anunciar</Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {stats.map(s => (
          <div key={s.label} className="p-4 rounded-xl border border-border bg-card" data-testid={`stat-${s.label}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${s.color}`}><s.icon className="h-4 w-4" /></div>
              <div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="listings" data-testid="tab-listings">Meus Anúncios</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">Vendas</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="tab-purchases">Compras</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites">Favoritos</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics" onClick={() => {
            if (!analytics) api.get('/escrow/seller-analytics').then(r => setAnalytics(r.data)).catch(() => {});
          }}>
            <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="listings">
          {listings.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold mb-1">Nenhum anúncio ainda</h3>
              <p className="text-sm text-muted-foreground mb-4">Crie seu primeiro anúncio e comece a vender!</p>
              <Link to="/create-listing"><Button className="rounded-full">Criar Anúncio</Button></Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="listings-grid">
              {listings.map(p => (
                <div key={p._id} className="flex flex-col">
                  <ProductCard product={p} />
                  <div className="flex gap-1.5 mt-2">
                    <Button variant="outline" size="sm" className="flex-1 gap-1.5 text-xs h-8 rounded-lg" onClick={() => navigate(`/edit-listing/${p._id}`)} data-testid={`edit-btn-${p._id}`}>
                      <Pencil className="h-3 w-3" /> Editar
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8 rounded-lg text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive" onClick={() => { setDeleteTarget(p); setDeleteDialog(true); }} data-testid={`delete-btn-${p._id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sales">
          {escrowSales.length === 0 && sales.length === 0 ? (
            <div className="text-center py-12"><TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><h3 className="font-semibold">Nenhuma venda ainda</h3></div>
          ) : (
            <div className="space-y-3" data-testid="sales-list">
              {/* Escrow Sales */}
              {escrowSales.map(tx => (
                <div key={tx._id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-border bg-card">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{tx.product_title}</p>
                    <p className="text-xs text-muted-foreground">Comprador: {tx.buyer_name}</p>
                    <p className="text-xs text-muted-foreground">
                      Preço: R$ {Number(tx.product_price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      {tx.platform_fee > 0 && <span> &middot; Taxa: -R$ {Number(tx.platform_fee).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>}
                      {tx.seller_fee > 0 && <span> &middot; Escrow: -R$ {Number(tx.seller_fee).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>}
                    </p>
                    <p className="text-xs font-medium text-green-600">Você recebe: R$ {Number(tx.seller_receives).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <p className="font-bold text-primary text-sm shrink-0">R$ {Number(tx.product_price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  <Badge className={escrowStatusColor[tx.status]}>{escrowStatusMap[tx.status]}</Badge>
                  <div className="flex gap-2 shrink-0">
                    {tx.status === 'paid' && (
                      <Button size="sm" onClick={() => handleEscrowAction(tx._id, 'shipped')} className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-xs rounded-lg" data-testid={`escrow-ship-${tx._id}`}>
                        <Package className="h-3.5 w-3.5" /> Enviei / Entreguei
                      </Button>
                    )}
                    {tx.status === 'completed' && !reviewedTxs.has(tx._id) && (
                      <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => openReview(tx, tx.buyer_id, tx.buyer_name)}>
                        <Star className="h-3.5 w-3.5" /> Avaliar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {/* Legacy Sales */}
              {sales.map(tx => (
                <div key={tx._id} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{tx.product_title}</p>
                    <p className="text-xs text-muted-foreground">Comprador: {tx.buyer_name}</p>
                  </div>
                  <p className="font-bold text-primary text-sm shrink-0">R$ {Number(tx.product_price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  <Badge className={statusColor[tx.status]}>{statusMap[tx.status]}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="purchases">
          {escrowPurchases.length === 0 && purchases.length === 0 ? (
            <div className="text-center py-12"><ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><h3 className="font-semibold">Nenhuma compra ainda</h3></div>
          ) : (
            <div className="space-y-3" data-testid="purchases-list">
              {/* Escrow Purchases */}
              {escrowPurchases.map(tx => (
                <div key={tx._id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-border bg-card">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{tx.product_title}</p>
                    <p className="text-xs text-muted-foreground">Vendedor: {tx.seller_name}</p>
                    {tx.status === 'disputed' && tx.dispute_reason && (
                      <p className="text-xs text-red-600 mt-1">Disputa: {tx.dispute_reason}</p>
                    )}
                  </div>
                  <p className="font-bold text-primary text-sm shrink-0">R$ {Number(tx.total_charged).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  <Badge className={escrowStatusColor[tx.status]}>{escrowStatusMap[tx.status]}</Badge>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    {tx.status === 'shipped' && (
                      <>
                        <Button size="sm" onClick={() => handleEscrowAction(tx._id, 'confirm_receipt')} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-xs rounded-lg" data-testid={`escrow-confirm-${tx._id}`}>
                          <MessageCircle className="h-3.5 w-3.5" /> Recebi e Aprovei
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDispute(tx._id)} className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50 text-xs rounded-lg" data-testid={`escrow-dispute-${tx._id}`}>
                          Problema
                        </Button>
                      </>
                    )}
                    {tx.status === 'awaiting_payment' && (
                      <Button size="sm" variant="outline" onClick={() => window.location.href = `/checkout/${tx.product_id}`} className="gap-1.5 text-xs rounded-lg">
                        Pagar
                      </Button>
                    )}
                    {tx.status === 'completed' && !reviewedTxs.has(tx._id) && (
                      <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => openReview(tx, tx.seller_id, tx.seller_name)}>
                        <Star className="h-3.5 w-3.5" /> Avaliar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {/* Legacy Purchases */}
              {purchases.map(tx => (
                <div key={tx._id} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{tx.product_title}</p>
                    <p className="text-xs text-muted-foreground">Vendedor: {tx.seller_name}</p>
                  </div>
                  <p className="font-bold text-primary text-sm shrink-0">R$ {Number(tx.product_price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  <Badge className={statusColor[tx.status]}>{statusMap[tx.status]}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="favorites">
          {favorites.length === 0 ? (
            <div className="text-center py-12"><Heart className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><h3 className="font-semibold">Nenhum favorito</h3></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="favorites-grid">
              {favorites.map(p => <ProductCard key={p._id} product={p} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="analytics">
          <SellerAnalytics data={analytics} />
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Avaliar {reviewTarget?.reviewed_name}</DialogTitle>
            <DialogDescription>Dê uma nota e deixe um comentário sobre a transação.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setReviewRating(n)}
                  onMouseEnter={() => setReviewHover(n)}
                  onMouseLeave={() => setReviewHover(0)}
                  className="transition-transform hover:scale-110"
                  data-testid={`star-${n}`}
                >
                  <Star className={`h-8 w-8 ${n <= (reviewHover || reviewRating) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
                </button>
              ))}
            </div>
            {reviewRating > 0 && (
              <p className="text-center text-sm text-muted-foreground">
                {['', 'Ruim', 'Regular', 'Bom', 'Muito Bom', 'Excelente'][reviewRating]}
              </p>
            )}
            <Textarea
              placeholder="Deixe um comentário (opcional)"
              value={reviewComment}
              onChange={e => setReviewComment(e.target.value)}
              rows={3}
              data-testid="review-comment-input"
            />
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setReviewDialog(false)}>Cancelar</Button>
            <Button onClick={submitReview} disabled={reviewRating < 1 || reviewSubmitting} className="gap-2" data-testid="submit-review-btn">
              {reviewSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
              Enviar Avaliação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Listing Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent data-testid="dashboard-delete-dialog">
          <DialogHeader>
            <DialogTitle>Remover Anúncio</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover o anúncio <span className="font-semibold">&ldquo;{deleteTarget?.title}&rdquo;</span>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setDeleteDialog(false); setDeleteTarget(null); }} disabled={deleting} data-testid="dashboard-cancel-delete-btn">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteListing} disabled={deleting} className="gap-2" data-testid="dashboard-confirm-delete-btn">
              <Trash2 className="h-4 w-4" /> {deleting ? 'Removendo...' : 'Sim, remover'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const fmt = v => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const METHOD_CONFIG = {
  pix: { label: 'PIX', icon: QrCode, color: '#14b8a6', bg: 'bg-teal-50 text-teal-700' },
  card: { label: 'Cartão', icon: CreditCard, color: '#3b82f6', bg: 'bg-blue-50 text-blue-700' },
  boleto: { label: 'Boleto', icon: FileText, color: '#f97316', bg: 'bg-orange-50 text-orange-700' },
  crypto: { label: 'Cripto', icon: Bitcoin, color: '#f59e0b', bg: 'bg-amber-50 text-amber-700' },
};

const POLICY_CONFIG = {
  buyer_pays_all: { label: 'Comprador paga juros', color: '#6366f1', desc: 'Até 12x com juros do comprador' },
  seller_absorbs_3x: { label: 'Sem juros até 3x', color: '#10b981', desc: 'Vendedor absorve juros até 3x' },
};

function BarChart({ data, maxValue }) {
  if (!data || data.length === 0) return null;
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  return (
    <div className="space-y-2.5">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-16 text-right shrink-0">{d.label}</span>
          <div className="flex-1 h-7 bg-muted rounded-lg overflow-hidden relative">
            <div
              className="h-full rounded-lg transition-all duration-700 ease-out flex items-center px-2"
              style={{ width: `${Math.max((d.value / max) * 100, 2)}%`, backgroundColor: d.color || '#3b82f6' }}
            >
              {(d.value / max) > 0.2 && <span className="text-[10px] font-bold text-white">{d.display || d.value}</span>}
            </div>
            {(d.value / max) <= 0.2 && <span className="absolute left-[calc(max(2%,16px)+8px)] top-1/2 -translate-y-1/2 text-[10px] font-medium text-muted-foreground">{d.display || d.value}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function DonutChart({ segments, size = 120 }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;
  const r = (size - 10) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  return (
    <svg width={size} height={size} className="mx-auto">
      {segments.map((seg, i) => {
        const pct = seg.value / total;
        const dash = circumference * pct;
        const gap = circumference - dash;
        const el = (
          <circle key={i} cx={c} cy={c} r={r} fill="none" stroke={seg.color} strokeWidth={10}
            strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-offset}
            className="transition-all duration-700" style={{ transformOrigin: 'center', transform: 'rotate(-90deg)' }} />
        );
        offset += dash;
        return el;
      })}
      <text x={c} y={c - 6} textAnchor="middle" className="text-lg font-bold fill-foreground">{total}</text>
      <text x={c} y={c + 10} textAnchor="middle" className="text-[10px] fill-muted-foreground">vendas</text>
    </svg>
  );
}

function SellerAnalytics({ data }) {
  if (!data) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Carregando analytics...</p>
      </div>
    );
  }

  if (data.total_sales === 0) {
    return (
      <div className="text-center py-12" data-testid="analytics-empty">
        <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
        <h3 className="font-semibold mb-1">Sem dados de vendas ainda</h3>
        <p className="text-sm text-muted-foreground">Quando suas primeiras vendas forem confirmadas, os analytics aparecerão aqui.</p>
      </div>
    );
  }

  const methodData = Object.entries(data.by_method).map(([key, val]) => ({
    label: METHOD_CONFIG[key]?.label || key,
    value: val.count,
    revenue: val.revenue,
    color: METHOD_CONFIG[key]?.color || '#94a3b8',
    display: `${val.count} (R$ ${fmt(val.revenue)})`,
  })).sort((a, b) => b.value - a.value);

  const methodDonut = Object.entries(data.by_method).map(([key, val]) => ({
    label: METHOD_CONFIG[key]?.label || key,
    value: val.count,
    color: METHOD_CONFIG[key]?.color || '#94a3b8',
  }));

  const policyData = Object.entries(data.by_policy).map(([key, val]) => ({
    label: POLICY_CONFIG[key]?.label || key,
    value: val.count,
    revenue: val.revenue,
    completed: val.completed,
    color: POLICY_CONFIG[key]?.color || '#94a3b8',
    display: `${val.count} vendas`,
    conversionPct: val.count > 0 ? Math.round((val.completed / val.count) * 100) : 0,
  }));

  const installmentData = Object.entries(data.by_installments || {}).map(([key, val]) => ({
    label: key,
    value: val.count,
    revenue: val.revenue,
    color: '#6366f1',
    display: `${val.count} (R$ ${fmt(val.revenue)})`,
  })).sort((a, b) => parseInt(a.label) - parseInt(b.label));

  return (
    <div className="space-y-6" data-testid="seller-analytics">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Total de Vendas" value={data.total_sales} icon={TrendingUp} color="text-emerald-600 bg-emerald-50" />
        <KPICard label="Receita Bruta" value={`R$ ${fmt(data.total_revenue)}`} icon={Coins} color="text-blue-600 bg-blue-50" />
        <KPICard label="Receita Líquida" value={`R$ ${fmt(data.net_revenue)}`} icon={ArrowUpRight} color="text-green-600 bg-green-50" sub={`-R$ ${fmt(data.total_fees)} total taxas (plataforma: R$ ${fmt(data.total_platform_fees || 0)})`} />
        <KPICard label="Taxa de Conversão" value={`${data.conversion_rate}%`} icon={Percent} color="text-purple-600 bg-purple-50" sub={`${data.total_sales}/${data.total_attempts} tentativas`} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* By Payment Method */}
        <div className="p-5 rounded-2xl border border-border bg-card" data-testid="analytics-by-method">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-primary" /> Vendas por Método de Pagamento
          </h3>
          <div className="flex items-start gap-6">
            <DonutChart segments={methodDonut} />
            <div className="flex-1 space-y-2 pt-2">
              {methodData.map(m => (
                <div key={m.label} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: m.color }} />
                  <span className="text-xs flex-1">{m.label}</span>
                  <span className="text-xs font-bold">{m.value}</span>
                  <span className="text-[10px] text-muted-foreground">R$ {fmt(m.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* By Installment Policy */}
        <div className="p-5 rounded-2xl border border-border bg-card" data-testid="analytics-by-policy">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" /> Conversão por Política de Parcelamento
          </h3>
          {policyData.length > 0 ? (
            <div className="space-y-4">
              {policyData.map(p => (
                <div key={p.label} className="p-3 rounded-xl border border-border bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                      <span className="text-sm font-medium">{p.label}</span>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">{p.display}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${p.conversionPct}%`, backgroundColor: p.color }} />
                    </div>
                    <span className="text-xs font-bold" style={{ color: p.color }}>{p.conversionPct}%</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">{p.completed} concluídas de {p.value} | R$ {fmt(p.revenue)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">Dados insuficientes</p>
          )}
        </div>
      </div>

      {/* Revenue by Method - Bar Chart */}
      <div className="p-5 rounded-2xl border border-border bg-card" data-testid="analytics-revenue-chart">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <ArrowUpRight className="h-4 w-4 text-primary" /> Receita por Método
        </h3>
        <BarChart data={methodData.map(m => ({ ...m, value: m.revenue, display: `R$ ${fmt(m.revenue)}` }))} />
      </div>

      {/* Installments Breakdown */}
      {installmentData.length > 0 && (
        <div className="p-5 rounded-2xl border border-border bg-card" data-testid="analytics-installments">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-primary" /> Vendas por Parcelas (Cartão)
          </h3>
          <BarChart data={installmentData} />
        </div>
      )}

      {/* Recent Sales Table */}
      {data.recent_sales?.length > 0 && (
        <div className="p-5 rounded-2xl border border-border bg-card" data-testid="analytics-recent-sales">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" /> Vendas Recentes
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 font-medium text-muted-foreground">Produto</th>
                  <th className="text-left py-2 font-medium text-muted-foreground">Comprador</th>
                  <th className="text-left py-2 font-medium text-muted-foreground">Método</th>
                  <th className="text-right py-2 font-medium text-muted-foreground">Valor</th>
                  <th className="text-right py-2 font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_sales.map(s => {
                  const mcfg = METHOD_CONFIG[s.payment_method] || {};
                  return (
                    <tr key={s._id} className="border-b border-border/50 last:border-0">
                      <td className="py-2.5 max-w-[160px] truncate font-medium">{s.product_title}</td>
                      <td className="py-2.5 text-muted-foreground">{s.buyer_name}</td>
                      <td className="py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${mcfg.bg || 'bg-muted'}`}>
                          {mcfg.label || s.payment_method}
                          {s.card_installments > 1 && ` ${s.card_installments}x`}
                        </span>
                      </td>
                      <td className="py-2.5 text-right font-bold">R$ {fmt(s.product_price)}</td>
                      <td className="py-2.5 text-right">
                        <Badge variant="secondary" className="text-[10px]">{s.status}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ticket Médio */}
      <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
        <p className="text-sm text-center">
          <span className="text-muted-foreground">Ticket médio: </span>
          <span className="font-bold text-primary text-lg">R$ {fmt(data.avg_ticket)}</span>
        </p>
      </div>
    </div>
  );
}

function KPICard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="p-4 rounded-xl border border-border bg-card" data-testid={`kpi-${label}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}><Icon className="h-4 w-4" /></div>
        <div>
          <p className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>{value}</p>
          <p className="text-[10px] text-muted-foreground">{label}</p>
          {sub && <p className="text-[10px] text-muted-foreground/70">{sub}</p>}
        </div>
      </div>
    </div>
  );
}
